import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/useAuthStore';

import Login from './pages/Login';
import Layout from './components/layout/Layout';
import Contacts from './pages/Contacts';
import ManagerDashboard from './pages/ManagerDashboard';
import MyPortfolio from './pages/MyPortfolio';
import UsersPage from './pages/UsersPage';
import Contact360View from './pages/Contact360View';
import LeadsBoard from './pages/LeadsBoard';
import ArchivedLeads from './pages/ArchivedLeads';
import DealsBoard from './pages/DealsBoard';
import AiStrategySettings from './pages/AiStrategySettings';
import ConfigPage from './pages/ConfigPage';
import Settings from './pages/Settings';
import BcvSettings from './pages/BcvSettings';
import ChatPage from './pages/ChatPage';
import MessageTemplatesPage from './pages/MessageTemplatesPage';
import InventorySync from './pages/InventorySync';
import AiSandbox from './pages/AiSandbox';
import TutorPage from './pages/TutorPage';
import Emails from './pages/Emails';
import QuotesPage from './pages/QuotesPage';
import FormsPage from './pages/FormsPage';
import CustomFieldsPage from './pages/CustomFieldsPage';
import NotificationSettings from './pages/NotificationSettings';
import QuoteBuilder from './pages/QuoteBuilder';
import TenantsPage from './pages/SuperAdmin/TenantsPage';
import SuperAdminInventorySync from './pages/SuperAdmin/SuperAdminInventorySync';
import RoleGuard from './components/common/RoleGuard';
import ModuleGuard from './components/common/ModuleGuard';
import LandingRedirect from './components/common/LandingRedirect';
import CampaignsPage from './pages/CampaignsPage';
import CampaignEditor from './pages/CampaignEditor';
import CampaignDetail from './pages/CampaignDetail';
import CampaignProtection from './pages/CampaignProtection';

// Protective Wrapper for specific routes
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Route that redirects logged-in users away from auth pages
const GuestRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
      </div>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/contacts" replace />;
};

function App() {
  const checkAuth = useAuthStore(state => state.checkAuth);

  // Attempt to restore user session on mount (validates Sanctum Token)
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={
            <GuestRoute>
              <Login />
            </GuestRoute>
          }
        />

        {/* Authenticated Routes wrapped in ProtectedRoute & Layout */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Default redirect based on role + módulos habilitados */}
          <Route path="/" element={<LandingRedirect />} />

          {/* Shared routes */}
          <Route path="/contacts" element={<ModuleGuard module="contacts"><Contacts /></ModuleGuard>} />
          <Route path="/contacts/:id/360" element={<ModuleGuard module="contacts"><Contact360View /></ModuleGuard>} />
          <Route path="/dashboard" element={<div className="p-8">Dashboard Content coming soon...</div>} />
          <Route path="settings/general" element={
            <RoleGuard roles={['admin', 'manager']}>
              <ModuleGuard module="settings" feature="general"><ConfigPage /></ModuleGuard>
            </RoleGuard>
          } />
          {/* settings es la pantalla de cuenta/perfil: no se restringe. */}
          <Route path="settings" element={<Settings />} />
          <Route path="settings/ai" element={<ModuleGuard module="settings" feature="ai"><AiStrategySettings /></ModuleGuard>} />
          <Route path="settings/bcv" element={<ModuleGuard module="settings" feature="bcv"><BcvSettings /></ModuleGuard>} />
          <Route path="settings/inventory" element={<ModuleGuard module="inventory"><InventorySync /></ModuleGuard>} />
          <Route path="settings/templates" element={<ModuleGuard module="templates"><MessageTemplatesPage /></ModuleGuard>} />
          <Route path="leads" element={<ModuleGuard module="leads"><LeadsBoard /></ModuleGuard>} />
          <Route path="leads/archived" element={<ModuleGuard module="leads" feature="archived"><ArchivedLeads /></ModuleGuard>} />
          <Route path="deals" element={<ModuleGuard module="deals"><DealsBoard /></ModuleGuard>} />
          <Route path="/chat" element={<ModuleGuard module="chat"><ChatPage /></ModuleGuard>} />
          <Route path="/tutor" element={<ModuleGuard module="tutor"><TutorPage /></ModuleGuard>} />
          <Route path="/emails" element={<ModuleGuard module="emails"><Emails /></ModuleGuard>} />
          <Route path="/quotes" element={<ModuleGuard module="quotes"><QuotesPage /></ModuleGuard>} />
          <Route path="/forms" element={<ModuleGuard module="forms"><FormsPage /></ModuleGuard>} />
          <Route path="settings/custom-fields" element={<ModuleGuard module="contacts" feature="custom_fields"><CustomFieldsPage /></ModuleGuard>} />
          <Route path="settings/notifications" element={<NotificationSettings />} />
          <Route path="/quotes/:id" element={<ModuleGuard module="quotes"><QuoteBuilder /></ModuleGuard>} />
          <Route path="/ai-sandbox" element={
            <RoleGuard roles={['admin', 'manager']}>
              <ModuleGuard module="sandbox"><AiSandbox /></ModuleGuard>
            </RoleGuard>
          } />

          {/* Vendedor (Agent) routes */}
          <Route path="/my-portfolio" element={<ModuleGuard module="portfolio"><MyPortfolio /></ModuleGuard>} />
          <Route path="/campaigns" element={<ModuleGuard module="campaigns"><CampaignsPage /></ModuleGuard>} />
          <Route path="/campaigns/new" element={<ModuleGuard module="campaigns"><CampaignEditor /></ModuleGuard>} />
          <Route path="/campaigns/protection" element={<ModuleGuard module="campaigns"><CampaignProtection /></ModuleGuard>} />
          <Route path="/campaigns/:id/edit" element={<ModuleGuard module="campaigns"><CampaignEditor /></ModuleGuard>} />
          <Route path="/campaigns/:id" element={<ModuleGuard module="campaigns"><CampaignDetail /></ModuleGuard>} />

          {/* Manager / Admin only routes */}
          <Route
            path="/manager"
            element={
              <RoleGuard roles={['admin', 'manager']}>
                <ModuleGuard module="dashboard"><ManagerDashboard /></ModuleGuard>
              </RoleGuard>
            }
          />
          <Route
            path="/users"
            element={
              <RoleGuard roles={['admin', 'manager']}>
                <ModuleGuard module="users"><UsersPage /></ModuleGuard>
              </RoleGuard>
            }
          />

          {/* ── Super Admin Routes ── */}
          <Route
            path="/super-admin/tenants"
            element={
              <RoleGuard roles={['super_admin']}>
                <TenantsPage />
              </RoleGuard>
            }
          />
          <Route
            path="/super-admin/sync"
            element={
              <RoleGuard roles={['super_admin']}>
                <SuperAdminInventorySync />
              </RoleGuard>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
